import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Mail, Instagram, Wallet, MessageSquare, MessageCircle, Trash2, CheckCircle, Circle, RefreshCw, Phone } from 'lucide-react';

const API = process.env.REACT_APP_API_URL;

export default function Kontakt() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const token = user?.token;

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread' | 'read'
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    setLoading(true);
    fetch(`${API}/api/contact-messages`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => { setMessages(data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [token]);

  const toggleRead = async (msg) => {
    await fetch(`${API}/api/contact-messages/${msg.id}/read`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ read: !msg.read })
    });
    setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, read: m.read ? 0 : 1 } : m));
  };

  const deleteMsg = async (id) => {
    if (!window.confirm('Nachricht wirklich löschen?')) return;
    await fetch(`${API}/api/contact-messages/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    setMessages(prev => prev.filter(m => m.id !== id));
    if (expanded === id) setExpanded(null);
  };

  const filtered = messages.filter(m => {
    if (filter === 'unread') return !m.read;
    if (filter === 'read') return m.read;
    return true;
  });

  const unreadCount = messages.filter(m => !m.read).length;

  const formatDate = (ts) => {
    const d = new Date(ts);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="section" style={{ minHeight: 'calc(100vh - 140px)' }}>
      <div className="container" style={{ maxWidth: '760px' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/privat')}
              className="icon-button"
              style={{ display: 'flex', gap: '6px', alignItems: 'center', color: 'var(--text-secondary)' }}
            >
              <ArrowLeft size={18} />
            </button>
            <h1 className="section-title" style={{ marginBottom: 0 }}>Kontaktanfragen</h1>
            {unreadCount > 0 && (
              <span style={{
                background: 'var(--accent)', color: '#000',
                fontSize: '11px', fontWeight: 700,
                padding: '2px 8px', borderRadius: '20px'
              }}>
                {unreadCount} neu
              </span>
            )}
          </div>
          <p className="section-subtitle" style={{ marginBottom: '32px' }}>
            Alle Nachrichten aus dem Kontaktformular.
          </p>

          {/* Filter bar */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', alignItems: 'center' }}>
            {['all', 'unread', 'read'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  padding: '6px 14px',
                  borderRadius: '6px',
                  border: `1px solid ${filter === f ? 'var(--accent)' : 'var(--border)'}`,
                  background: filter === f ? 'rgba(251,146,60,0.1)' : 'transparent',
                  color: filter === f ? 'var(--accent)' : 'var(--text-secondary)',
                  fontSize: '13px', fontWeight: 500, cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {f === 'all' ? 'Alle' : f === 'unread' ? 'Ungelesen' : 'Gelesen'}
              </button>
            ))}
            <button
              onClick={load}
              className="icon-button"
              style={{ marginLeft: 'auto', color: 'var(--text-secondary)' }}
              title="Aktualisieren"
            >
              <RefreshCw size={16} />
            </button>
          </div>

          {/* Messages */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
              Laden...
            </div>
          ) : filtered.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '60px 0',
              color: 'var(--text-secondary)', border: '1px dashed var(--border)',
              borderRadius: '12px'
            }}>
              <MessageSquare size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
              <p>{filter === 'unread' ? 'Keine ungelesenen Nachrichten.' : 'Noch keine Nachrichten.'}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <AnimatePresence>
                {filtered.map((msg, i) => (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.25, delay: i * 0.04 }}
                    style={{
                      background: 'var(--card-bg, #111)',
                      border: `1px solid ${!msg.read ? 'rgba(251,146,60,0.3)' : 'var(--border)'}`,
                      borderRadius: '10px',
                      overflow: 'hidden',
                      transition: 'border-color 0.2s'
                    }}
                  >
                    {/* Row header */}
                    <div
                      style={{
                        display: 'flex', alignItems: 'center', gap: '12px',
                        padding: '14px 16px', cursor: 'pointer',
                        background: expanded === msg.id ? 'rgba(255,255,255,0.02)' : 'transparent'
                      }}
                      onClick={() => {
                        setExpanded(expanded === msg.id ? null : msg.id);
                        if (!msg.read) toggleRead(msg);
                      }}
                    >
                      {/* Read indicator */}
                      <div style={{ flexShrink: 0 }}>
                        {msg.read
                          ? <CheckCircle size={16} color="var(--text-secondary)" />
                          : <Circle size={16} color="var(--accent)" fill="rgba(251,146,60,0.2)" />
                        }
                      </div>

                      {/* Name + preview */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                          <span style={{ fontWeight: msg.read ? 400 : 600, fontSize: '14px', color: 'var(--text-primary, #f5f5f5)' }}>
                            {msg.name}
                          </span>
                          {(msg.prefer_instagram && msg.instagram) && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--accent)', background: 'rgba(251,146,60,0.1)', padding: '1px 6px', borderRadius: '4px' }}>
                              <Instagram size={10} />Instagram
                            </span>
                          )}
                          {msg.phone_type === 'whatsapp' && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#25d366', background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.3)', padding: '1px 6px', borderRadius: '4px' }}>
                              <MessageCircle size={10} />WhatsApp
                            </span>
                          )}
                          {msg.phone_type === 'messenger' && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#0099ff', background: 'rgba(0,153,255,0.1)', border: '1px solid rgba(0,153,255,0.3)', padding: '1px 6px', borderRadius: '4px' }}>
                              <MessageSquare size={10} />Messenger
                            </span>
                          )}
                          {msg.phone_type === 'anruf' && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--accent)', background: 'rgba(251,146,60,0.1)', padding: '1px 6px', borderRadius: '4px' }}>
                              <Phone size={10} />Anruf
                            </span>
                          )}
                          {msg.budget && (
                            <span style={{ fontSize: '11px', fontWeight: 700, color: '#f87171', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', padding: '1px 7px', borderRadius: '4px' }}>
                              Budget: €{msg.budget}
                            </span>
                          )}
                        </div>
                        <p style={{
                          margin: 0, fontSize: '13px', color: 'var(--text-secondary)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                        }}>
                          {msg.message}
                        </p>
                      </div>

                      {/* Date */}
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)', flexShrink: 0 }}>
                        {formatDate(msg.created_at)}
                      </span>
                    </div>

                    {/* Expanded detail */}
                    <AnimatePresence>
                      {expanded === msg.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          style={{ overflow: 'hidden' }}
                        >
                          <div style={{
                            padding: '16px 16px 20px',
                            borderTop: '1px solid var(--border)',
                            display: 'flex', flexDirection: 'column', gap: '14px'
                          }}>
                            {/* Contact info */}
                            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                              <a
                                href={`mailto:${msg.email}`}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--accent)', textDecoration: 'none' }}
                              >
                                <Mail size={14} />
                                {msg.email}
                              </a>
                              {msg.instagram && (
                                <a
                                  href={`https://instagram.com/${msg.instagram}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--accent)', textDecoration: 'none' }}
                                >
                                  <Instagram size={14} />
                                  @{msg.instagram}
                                  {msg.prefer_instagram && <span style={{ fontSize: '10px', background: 'rgba(251,146,60,0.15)', border: '1px solid rgba(251,146,60,0.3)', padding: '1px 5px', borderRadius: '3px' }}>bevorzugt</span>}
                                </a>
                              )}
                              {msg.phone && (() => {
                                const cleanPhone = msg.phone.replace(/\s+/g, '');
                                const isWA = msg.phone_type === 'whatsapp';
                                const isMes = msg.phone_type === 'messenger';
                                const href = isWA
                                  ? `https://wa.me/${cleanPhone.replace('+', '')}`
                                  : isMes
                                  ? `https://m.me/`  // Messenger needs username, fallback to tel
                                  : `tel:${msg.phone}`;
                                const color = isWA ? '#25d366' : isMes ? '#0099ff' : 'var(--accent)';
                                const icon = isWA ? <MessageCircle size={14} /> : isMes ? <MessageSquare size={14} /> : null;
                                const typeLabel = isWA ? 'WhatsApp' : isMes ? 'Messenger' : msg.phone_type === 'anruf' ? 'Anruf' : null;
                                return (
                                  <a
                                    href={href}
                                    target={isWA ? '_blank' : undefined}
                                    rel={isWA ? 'noopener noreferrer' : undefined}
                                    style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color, textDecoration: 'none' }}
                                  >
                                    {icon || <Phone size={14} />}
                                    {msg.phone}
                                    {typeLabel && (
                                      <span style={{ fontSize: '10px', background: `${color}22`, padding: '1px 5px', borderRadius: '3px', border: `1px solid ${color}44` }}>
                                        {typeLabel}
                                      </span>
                                    )}
                                  </a>
                                );
                              })()}
                              {msg.budget && (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#f87171' }}>
                                  <Wallet size={14} />
                                  Budget: €{msg.budget}
                                </span>
                              )}
                            </div>

                            {/* Message */}
                            <p style={{
                              margin: 0, fontSize: '14px', lineHeight: 1.65,
                              color: 'var(--text-primary, #ddd)',
                              whiteSpace: 'pre-wrap',
                              background: 'rgba(255,255,255,0.03)',
                              padding: '12px 14px',
                              borderRadius: '6px',
                              border: '1px solid var(--border)'
                            }}>
                              {msg.message}
                            </p>

                            {/* Actions */}
                            <div style={{ display: 'flex', gap: '10px' }}>
                              <button
                                onClick={() => toggleRead(msg)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '6px',
                                  padding: '6px 12px', borderRadius: '6px',
                                  border: '1px solid var(--border)',
                                  background: 'transparent',
                                  color: 'var(--text-secondary)',
                                  fontSize: '12px', cursor: 'pointer'
                                }}
                              >
                                {msg.read ? <Circle size={13} /> : <CheckCircle size={13} />}
                                {msg.read ? 'Als ungelesen markieren' : 'Als gelesen markieren'}
                              </button>
                              <button
                                onClick={() => deleteMsg(msg.id)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '6px',
                                  padding: '6px 12px', borderRadius: '6px',
                                  border: '1px solid rgba(239,68,68,0.3)',
                                  background: 'rgba(239,68,68,0.05)',
                                  color: '#f87171',
                                  fontSize: '12px', cursor: 'pointer'
                                }}
                              >
                                <Trash2 size={13} />
                                Löschen
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
